import React, { useState, useEffect } from 'react';

interface QRCodeImageProps {
  qrCodeUrl: string;
  alt?: string;
  className?: string;
  maxRetries?: number;
  retryDelay?: number;
}

const QRCodeImage: React.FC<QRCodeImageProps> = ({
  qrCodeUrl,
  alt = "QR Code",
  className = "w-48 h-48 border border-gray-300 rounded",
  maxRetries = 5,
  retryDelay = 3000
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  const checkImageAvailability = async (url: string): Promise<boolean> => {
    return new Promise((resolve) => {
      const img = new Image();
      
      img.onload = () => {
        resolve(true);
      };
      
      img.onerror = () => {
        resolve(false);
      };
      
      // Set a timeout for the image load attempt
      setTimeout(() => {
        resolve(false);
      }, 5000); // 5 second timeout for each attempt
      
      img.src = url;
    });
  };

  const attemptToLoadImage = async () => {
    const isAvailable = await checkImageAvailability(qrCodeUrl);
    
    if (isAvailable) {
      setImageUrl(qrCodeUrl);
      setIsLoading(false);
      setHasError(false);
    } else {
      const nextRetryCount = retryCount + 1;
      setRetryCount(nextRetryCount);
      
      if (nextRetryCount >= maxRetries) {
        setIsLoading(false);
        setHasError(true);
      } else {
        setTimeout(() => {
          attemptToLoadImage();
        }, retryDelay);
      }
    }
  };

  useEffect(() => {
    if (!qrCodeUrl) {
      setHasError(true);
      setIsLoading(false);
      return;
    }

    // Reset state when URL changes
    setIsLoading(true);
    setHasError(false);
    setRetryCount(0);
    setImageUrl(null);
    
    // Start the retry process
    attemptToLoadImage();
  }, [qrCodeUrl, maxRetries, retryDelay]);

  // Don't render anything if there's an error (same behavior as before)
  if (hasError) {
    return null;
  }

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center space-y-2">
        <div className={`${className} flex items-center justify-center bg-gray-100`}>
          <div className="flex flex-col items-center space-y-2">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="text-xs text-gray-600">
              Loading QR Code... ({retryCount + 1}/{maxRetries})
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Show the actual image once loaded
  return (
    <div className="flex flex-col items-center space-y-2">
      <img
        src={imageUrl!}
        alt={alt}
        className={className}
        onError={() => {
          setHasError(true);
        }}
      />
    </div>
  );
};

export default QRCodeImage; 